import { FileText, Loader2 } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { renderAsync } from "docx-preview";
import { cn } from "@/modern/utils";

export interface WordViewerProps {
  fileUrl: string;
  fileName?: string;
  showName?: boolean;
  className?: string;
  onLoadSuccess?: () => void;
  onLoadError?: (error: Error) => void;
  isThumbnail?: boolean;
}

const WordViewer = ({ fileUrl, fileName, showName = false, className = "", onLoadSuccess, onLoadError, isThumbnail = false }: WordViewerProps) => {
  const [wordError, setWordError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const previewContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadDocument = async () => {
      if (!previewContainerRef.current || !fileUrl) return;

      try {
        setIsLoading(true);
        setWordError(null);
        previewContainerRef.current.innerHTML = "";

        const response = await fetch(fileUrl, { credentials: 'include' });
        if (!response.ok) {
          throw new Error(`Failed to fetch document: ${response.status} ${response.statusText}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);

        if (uint8Array.length < 4 || (uint8Array[0] !== 0x50 || uint8Array[1] !== 0x4B)) {
          throw new Error("Invalid file format. Expected DOCX file.");
        }

        await renderAsync(arrayBuffer, previewContainerRef.current, undefined, {
          className: "docx-wrapper",
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          useBase64URL: true,
          renderHeaders: true,
          renderFooters: true,
        });

        setIsLoading(false);
        onLoadSuccess?.();
      } catch (error) {
        console.error("Word document load error:", error);
        const errorMessage = error instanceof Error ? error.message : "Failed to load Word document";
        setWordError(errorMessage);
        setIsLoading(false);
        onLoadError?.(error instanceof Error ? error : new Error(errorMessage));
      }
    };

    loadDocument();
  }, [fileUrl, onLoadSuccess, onLoadError]);

  if (isThumbnail) {
    return (
      <div className={cn("relative w-full h-full", className)}>
        {wordError ? (
          <div className="flex flex-col items-center justify-center text-muted-foreground p-4">
            <FileText className="size-12 mb-2" />
            <span className="text-xs text-center">{wordError}</span>
          </div>
        ) : (
          <>
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
                <Loader2 className="size-4 animate-spin" />
              </div>
            )}
            <div ref={previewContainerRef} className="w-full h-full overflow-auto" />
          </>
        )}
      </div>
    );
  }

  return (
    <div className={cn("relative flex flex-col w-full h-full overflow-y-auto [&_.docx-wrapper]:last:!mb-0 [&_.docx-wrapper-wrapper]:!p-4 [&_.docx-wrapper]:!w-auto [&_.docx-wrapper]:!p-8", className)}>
      {wordError ? (
        <div className="flex flex-col items-center justify-center text-muted-foreground py-16">
          <FileText className="size-16 mb-4 opacity-50" />
          <span className="text-sm font-medium text-center max-w-md">{wordError}</span>
        </div>
      ) : (
        <div className="w-full max-w-4xl relative mx-auto">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10 min-h-[400px]">
              <Loader2 className="size-4 animate-spin" />
            </div>
          )}
          <div ref={previewContainerRef} className="w-full" />
        </div>
      )}

      {showName && fileName && (
        <div className="flex-shrink-0 border-t border-border bg-background/95">
          <span className="block text-sm text-center py-3 px-4 font-medium truncate">{fileName}</span>
        </div>
      )}
    </div>
  );
};

export default WordViewer;
